import dayjs from "dayjs";
import type {
  LogItem,
  LogTimeByDateResponse,
  WorkerLogtimeChangeHistory,
} from "@lhd/types";
import { dayMemoFromApiLogtimes } from "../../../dayMemoFromApiLogtimes";
import {
  formatIsoInTokyo,
  nowInTokyo,
  workDateTimeToLoggedAtIso,
} from "../../../workLogTimezone";
import type { WorkerLogtimesSaveJsonItem } from "@lhd/hooks";

export type PunchLogType =
  | "check_in"
  | "check_out"
  | "break_start"
  | "break_end";

export const PUNCH_TYPE_LABELS: Record<PunchLogType, string> = {
  check_in: "出勤",
  break_start: "休憩開始",
  break_end: "休憩終了",
  check_out: "退勤",
};

export type AttendanceDraftRow = {
  key: string;
  id?: number;
  logType: PunchLogType;
  time: string;
  deleted: boolean;
  originalLogType?: PunchLogType;
  originalTime?: string;
  initialLogType?: PunchLogType;
  initialTime?: string;
};

export type AttendanceEditorDraft = {
  rows: AttendanceDraftRow[];
  memo: string;
};

export const enrichLogtimesWithOriginalValues = (
  data: LogTimeByDateResponse,
): LogTimeByDateResponse => {
  const histories = [...(data.change_histories ?? [])].sort(
    (left, right) =>
      dayjs(left.created_at).valueOf() - dayjs(right.created_at).valueOf(),
  );

  return {
    ...data,
    logtimes: data.logtimes?.map((logtime) => {
      const firstHistory = histories.find(
        (history) => history.worker_logtime_id === logtime.id,
      );
      const wasCreatedInEditor = firstHistory?.change_type === "created";

      return {
        ...logtime,
        originalLogType: firstHistory
          ? wasCreatedInEditor
            ? firstHistory.after_log_type
            : firstHistory.before_log_type
          : logtime.log_type,
        originalTime: firstHistory
          ? wasCreatedInEditor
            ? firstHistory.after_logged_at
            : firstHistory.before_logged_at
          : logtime.logged_at,
      };
    }),
  };
};

const isPunchLogType = (value: string): value is PunchLogType =>
  value === "check_in" ||
  value === "check_out" ||
  value === "break_start" ||
  value === "break_end";

const normalizedMemo = (value: string): string =>
  value.replace(/\r\n/g, "\n").trimEnd();

const toExtendedDisplayTime = (loggedAt: string, workDate: string): string => {
  const localDate = formatIsoInTokyo(loggedAt, "YYYY-MM-DD");
  const localTime = formatIsoInTokyo(loggedAt, "HH:mm");
  const dayOffset = dayjs(localDate).diff(dayjs(workDate), "day");
  const hour = Number(localTime.slice(0, 2)) + dayOffset * 24;

  return Number.isInteger(hour) && hour >= 0 && hour < 100
    ? `${String(hour).padStart(2, "0")}:${localTime.slice(3)}`
    : localTime;
};

export const createAttendanceEditorDraft = (
  data: LogTimeByDateResponse,
): AttendanceEditorDraft => ({
  rows: (data.logtimes ?? [])
    .filter((logtime) => isPunchLogType(logtime.log_type))
    .sort(
      (left, right) =>
        dayjs(left.logged_at).valueOf() - dayjs(right.logged_at).valueOf(),
    )
    .map((logtime) => {
      const time = toExtendedDisplayTime(logtime.logged_at, data.date);
      return {
        key: `persisted-${logtime.id}`,
        id: logtime.id,
        logType: logtime.log_type as PunchLogType,
        time,
        deleted: false,
        originalLogType: logtime?.originalLogType as PunchLogType,
        originalTime: toExtendedDisplayTime(
          logtime.originalTime || logtime.logged_at,
          data.date,
        ),
        initialLogType: logtime.log_type as PunchLogType,
        initialTime: time,
      };
    }),
  memo: dayMemoFromApiLogtimes(data.logtimes ?? []),
});

export const inferNextPunchType = (
  rows: AttendanceDraftRow[],
): PunchLogType => {
  const activeRows = rows.filter((row) => !row.deleted);
  const hasCheckIn = activeRows.some((row) => row.logType === "check_in");
  const hasCheckOut = activeRows.some((row) => row.logType === "check_out");
  const lastLogType = activeRows.at(-1)?.logType;
  if (!hasCheckIn) return "check_in";
  if (!hasCheckOut && lastLogType === "break_end") return "check_out";
  if (lastLogType === "break_start") return "break_end";
  return "break_start";
};

export const validateAttendanceDraft = (
  workDate: string,
  rows: AttendanceDraftRow[],
  nowMs = nowInTokyo().valueOf(),
): string[] => {
  const active = rows.filter((row) => !row.deleted);
  if (active.length === 0) return [];

  const errors: string[] = [];
  const timestamps: number[] = [];

  active.forEach((row, index) => {
    const iso = workDateTimeToLoggedAtIso(workDate, row.time);
    if (!iso) {
      errors.push(`${index + 1}行目の時刻を入力してください。`);
      return;
    }

    const value = dayjs(iso).valueOf();
    timestamps.push(value);

    if (value > nowMs) {
      errors.push(`${index + 1}行目は未来の時刻のため保存できません。`);
    }
  });

  if (timestamps.length === active.length) {
    for (let index = 1; index < timestamps.length; index += 1) {
      if (timestamps[index] < timestamps[index - 1]) {
        errors.push("打刻時刻を早い順に入力してください。");
        break;
      }
    }
  }

  let state: "waiting" | "working" | "break" | "closed" = "waiting";

  for (const row of active) {
    const isValid =
      (state === "waiting" && row.logType === "check_in") ||
      (state === "working" &&
        (row.logType === "break_start" || row.logType === "check_out")) ||
      (state === "break" &&
        (row.logType === "break_end" || row.logType === "check_out"));

    if (!isValid) {
      errors.push("打刻の順序が正しくありません。");
      break;
    }

    if (row.logType === "check_in" || row.logType === "break_end") {
      state = "working";
    }

    if (row.logType === "break_start") {
      state = "break";
    }

    if (row.logType === "check_out") {
      state = "closed";
    }
  }

  return Array.from(new Set(errors));
};

const rowChanged = (row: AttendanceDraftRow): boolean =>
  row.id == null ||
  row.deleted ||
  row.logType !== row.initialLogType ||
  row.time !== row.initialTime;

export const buildAttendanceSaveItems = (
  data: LogTimeByDateResponse,
  draft: AttendanceEditorDraft,
): WorkerLogtimesSaveJsonItem[] => {
  const items: WorkerLogtimesSaveJsonItem[] = [];
  const memoChanged =
    normalizedMemo(draft.memo) !==
    normalizedMemo(dayMemoFromApiLogtimes(data.logtimes ?? []));

  draft.rows.forEach((row) => {
    if (row.deleted) {
      if (row.id != null) items.push({ id: row.id, _destroy: true });
      return;
    }
    const changed = rowChanged(row);
    const original = (data.logtimes ?? []).find(
      (logtime) => logtime.id === row.id,
    );
    const shouldClearLegacyNote =
      memoChanged &&
      row.id != null &&
      original?.log_type !== "note" &&
      normalizedMemo(String(original?.note ?? "")).trim() !== "";
    if (!changed && !shouldClearLegacyNote) return;
    if (!changed && shouldClearLegacyNote) {
      items.push({ id: row.id, note: null });
      return;
    }
    const loggedAt = workDateTimeToLoggedAtIso(data.date, row.time);
    if (!loggedAt) return;
    items.push({
      ...(row.id == null ? {} : { id: row.id }),
      log_type: row.logType,
      logged_at: loggedAt,
      ...(shouldClearLegacyNote ? { note: null } : {}),
    });
  });

  if (memoChanged) {
    (data.logtimes ?? [])
      .filter((logtime) => logtime.log_type === "note")
      .forEach((logtime) => items.push({ id: logtime.id, _destroy: true }));
    if (normalizedMemo(draft.memo).trim() !== "") {
      items.push({ log_type: "note", note: draft.memo });
    }
  }

  return items;
};

const historyValue = (
  logType: LogItem["log_type"] | null,
  displayTime: string | null,
): string => {
  if (!logType || logType === "note") return "メモ";
  const label = PUNCH_TYPE_LABELS[logType as PunchLogType] ?? String(logType);
  return `${label} ${displayTime || "--:--"}`;
};

export const formatAttendanceHistory = (
  history: WorkerLogtimeChangeHistory,
): string => {
  if (history.change_type === "created") {
    return `追加: ${historyValue(
      history.after_log_type,
      history.after_display_time,
    )}`;
  }
  if (history.change_type === "deleted") {
    return `削除: ${historyValue(
      history.before_log_type,
      history.before_display_time,
    )}`;
  }
  if (
    history.before_log_type === "note" ||
    history.after_log_type === "note" ||
    history.before_note !== history.after_note
  ) {
    return "メモを修正";
  }
  return `元: ${historyValue(
    history.before_log_type,
    history.before_display_time,
  )} → 修正: ${historyValue(
    history.after_log_type,
    history.after_display_time,
  )}`;
};
